import type { HTMLAttributes } from "svelte/elements";
import type { Country } from "../schema/Country.js";
import { DeliveryType } from "../schema/DeliveryType.js";
import type { Locale } from "../schema/Locale.js";
import type { countryToIsoCodeMap } from "../schema/utils/countryToIsoCodeMap.js";

/**
 * bg	Bulharština
 * cs	Čeština
 * de	Němčina
 * en	Angličtina
 * es	Španělština
 * fr	Francouzština
 * hr	Chorvatština
 * hu	Maďarština
 * it	Italština
 * nl	Nizozemština
 * no	Norština
 * pl	Polština
 * ro	Rumunština
 * sk	Slovenština
 *
 * Interesecteed with {@link Locale}
 */
export type PPLLanguage = Extract<
	Locale,
	"bg" | "cs" | "de" | "en" | "es" | "fr" | "hr" | "hu" | "it" | "nl" | "no" | "pl" | "ro" | "sk"
>;

/**
 *
 * Kompletní seznam parametrů předávaných přes atribut {@link PPLWidgetAttributes.config `config`} nebo property {@link HTMLPPLAccessPointWidgetElement.widgetConfig `widgetConfig`}.
 * Parametry označené **(administrace)** obvykle nastavuje administrace widgetu dle api-key — přes config je přepisujte jen když se konkrétní stránka musí lišit od globálního nastavení.
 *
 * @see https://ppl-widget2.apidog.io/7-kompletn%C3%AD-api-reference-2029223m0#75-konfigura%C4%8Dn%C3%AD-parametry-widgetconfig
 */
export interface WidgetConfig {
	// #region Obecné / UI
	/**
	 * Téma widgetu
	 * @default "PPL_THEME"
	 */
	theme?: string;
	/**
	 * **(administrace)** Režim zobrazení — výchozí hodnota widgetu je modal; administrace může
	 * přepnout na inline. Atribut config na elementu má nejvyšší prioritu.
	 */
	viewMode?: "modal" | "inline";
	/**
	 * Zobrazení tlačítka pro zavření
	 * @default –
	 */
	closeButtonVisible?: boolean;
	defaultLang?: Locale;
	/** Zobrazení dropdown menu pro výběr jazyka. */
	langMenu?: boolean;
	/** **(administrace)** Výchozí země (ISO 3166-1 alpha-2) */
	defaultCountry?: (typeof countryToIsoCodeMap)[Country];
	/** **(administrace)** Povolené země (průnik se serverovou konfigurací) */
	allowedCountries?: (typeof countryToIsoCodeMap)[Country][];
	/**
	 * Vypnutí menu se zeměmi ve filtru
	 * @default false
	 */
	countriesMenuDisabled?: boolean;
	// #endregion
	// #region Poloha mapy
	/** Slovní adresa pro vystředění mapy */
	centeredToAddress?: string;
	/** Zeměpisná šířka (WGS84) */
	centeredToLat?: number;
	/** Zeměpisná délka (WGS84) */
	centeredToLon?: number;
	// #endregion
	// #region Balení zásilky
	/** Váha balení zásilky (kg) */
	packageWeight?: number;
	/** Šířka balení zásilky (cm) */
	packageWidth?: number;
	/** Výška balení zásilky (cm) */
	packageHeight?: number;
	/** Délka balení zásilky (cm) */
	packageLength?: number;
	// #endregion
	// #region Služby
	/** Nutnost platby dobírky */
	codRequired?: boolean;
	/** Požadavek na ověření věku */
	ageCheck?: boolean;
	// #endregion
	// #region Výdejní místa
	/**  KM kód výdejního místa pro předvybrání (formát KM + 7 číslic, např. KM1234567) */
	accessPointCode?: string;
	/** **(administrace)** Zakázané typy výdejních míst — config sjednocuje se serverovým seznamem */
	disabledAccessPointTypes?: PPLAccessPointType[];
	/** **(administrace)** Ekonomický režim (Smart2Box) — hodnotu ze serveru nelze přepsat */
	economyMode?: boolean;
	// #endregion
}

export enum PPLAccessPointType {
	/** Obsluhované výdejní místo (PPL Parcel Shop) */
	ParcelShop = "ParcelShop",
	/** Samoobslužný výdejní box (PPL Parcel Box) */
	ParcelBox = "ParcelBox",
	/** AlzaBox terminál (partnerský) */
	AlzaBox = "AlzaBox",
}

export interface DetailResponseModel {
	/** KM kód výdejního místa, např. "KM1234567" */
	code: string;
	/** Název výdejního místa */
	name: string;
	/**
	 * Kód typu výdejního místa z číselníku `/v1/codelists/accessPointTypes`,
	 * např. "ALZA_BOX", "PPL_BOX_V2", "PPL_BOX_V3". PPL kódy verzuje, proto na ně
	 * nespoléhejte jako na uzavřený výčet — použijte {@link getDeliveryTypeFromPplResponse}.
	 *
	 * Pozor: nejde o hodnoty {@link PPLAccessPointType}. Ty odpovídají poli `name`
	 * číselníku a uplatní se jen u {@link WidgetConfig.disabledAccessPointTypes}.
	 *
	 * Vyplněné jen u výběru z detailu; položky seznamu nesou místo něj
	 * {@link DetailResponseModel.accessPointType}.
	 */
	type?: string;
	/** Totéž co {@link DetailResponseModel.type}, ale u výběru ze seznamu výdejních míst. */
	accessPointType?: string;
	/** Adresa výdejního místa */
	address?: {
		/** Ulice s číslem popisným */
		street: string;
		/** Město */
		city: string;
		/** PSČ */
		zipCode: string;
		/** Kód země (ISO 3166-1 alpha-2) */
		countryCode: string;
		/** GPS souřadnice */
		gps: {
			/** Zeměpisná šířka (WGS84) */
			lat: number;
			/** Zeměpisná délka (WGS84) */
			lon: number;
		};
	};
	/** Otevírací hodiny */
	openHours?: Record<
		"regular" | "exceptional",
		{
			/** 1 = pondělí, 7 = neděle */
			day: number;
			/** Je otevřeno */
			open: boolean;
			/** Nonstop provoz */
			nonstop: boolean;
			/** Časové intervaly (formát "HH:mm") */
			intervals: [{ from: string; to: string }];
		}[]
	>;
	/** Přijaté platební metody */
	paymentMethods?: {
		/** Hotovost */
		cash: boolean;
		/** Platební karta */
		card: boolean;
		/** Nutná online platba */
		onlineRequired: boolean;
	};
	/** Doplňující poznámka */
	additionalNote?: string;
	/**
	 * Dostupnost a přístupnost
	 *
	 * @property walk Pěší dostupnost
	 * @property parking Parkování
	 * @property publicTransport Veřejná doprava
	 */
	accessibility?: Record<"parking" | "publicTransport" | "walk", { available: boolean }>;
	/**
	 * Aktivní funkce
	 *
	 * @property map Zobrazeno na mapě
	 * @property dropOff Podání zásilek
	 * @property pickUpOrder Vyzvednutí zásilek
	 * @property vacationMode Prázdninový režim
	 */
	activeFeatures?: Record<"map" | "dropOff" | "pickUpOrder" | "vacationMode", boolean>;
	/** Kapacita */
	capacityStatus?: "Unknown" | "Free" | "NearlyFull" | "Full";
	/** Přijímané velikosti, např. ["S", "M", "L", "XL"] */
	acceptedSizes?: string[];
	/** Nově otevřené místo */
	newAccessPoint?: boolean;
	/** URL cesta na detail místa */
	urlPath?: string;
	/** Bezbariérový přístup */
	barrierFree?: boolean;
	/** Vzdálenost od středu mapy v km (jen u položek seznamu) */
	distance?: number;
}

export interface HTMLPPLAccessPointWidgetElement extends HTMLElement {
	readonly version: string;
	widgetConfig: WidgetConfig;
	/**
	 * Otevře widget (pouze v modal režimu)
	 */
	open(): void;
	/**
	 * Zavře widget (pouze v modal režimu)
	 */
	close(): void;
	/**
	 * Resetuje widget do výchozího stavu
	 */
	reset(): void;
	/**
	 * Vrátí vybrané výdejní místo nebo null
	 */
	getSelectedAccessPoint(): DetailResponseModel | null;
}

export interface PPLAccessPointWidgetEventDetailMap {
	"ppl-accesspointwidget-select": DetailResponseModel;
	"ppl-accesspointwidget-close": null;
	"ppl-accesspointwidget-error": { code: string; message: string };
	"ppl-accesspointwidget-ready": null;
}

export type PPLAccessPointWidgetEventName = keyof PPLAccessPointWidgetEventDetailMap;

export interface PPLAccessPointWidgetEvent<Name extends PPLAccessPointWidgetEventName> extends CustomEvent<
	PPLAccessPointWidgetEventDetailMap[Name]
> {
	name: Name;
	bubbles: true;
	composed: true;
}

type HTMLEventNameMapExtensions = {
	[Name in PPLAccessPointWidgetEventName]: PPLAccessPointWidgetEvent<Name>;
};

export type PPLAccessPointWidgetEventAttributes = {
	[Name in PPLAccessPointWidgetEventName as `on${Name}`]?: (
		this: HTMLPPLAccessPointWidgetElement,
		event: PPLAccessPointWidgetEvent<Name>,
	) => void;
};

export interface PPLWidgetAttributes extends HTMLAttributes<HTMLElement>, PPLAccessPointWidgetEventAttributes {
	"api-key": string;
	/**
	 * JSON serializace {@link WidgetConfig}
	 */
	config: string;
}

declare global {
	interface HTMLElementTagNameMap {
		"ppl-access-point-widget": HTMLPPLAccessPointWidgetElement;
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-object-type
	interface HTMLElementEventMap extends HTMLEventNameMapExtensions {}
}

declare module "svelte/elements" {
	export interface SvelteElement {
		"ppl-access-point-widget": PPLWidgetAttributes;
	}
}

const pplWidgetScriptUrl = "https://www.ppl.cz/accesspointwidget/loader.js";
const pplWidgetDefinitionTimeoutMs = 15_000;

/**
 * If necessary, loads the PPL widget script from the CDN.
 * Waits till the custom element is defined.
 *
 * @returns A promise that resolves when the script is loaded.
 */
export async function ensurePplAccessPointWidgetLoaded(): Promise<void> {
	if (customElements.get("ppl-access-point-widget")) {
		return;
	}
	if (!document.querySelector(`script[src="${pplWidgetScriptUrl}"]`)) {
		await new Promise<void>((resolve, reject) => {
			const script = document.createElement("script");
			script.async = true;
			script.addEventListener(
				"load",
				() => {
					resolve();
				},
				{ once: true },
			);
			script.addEventListener(
				"error",
				(event) => {
					script.remove();
					if (event.error instanceof Error) {
						reject(event.error);
					} else {
						reject(new Error(event.message));
					}
				},
				{ once: true },
			);
			script.src = pplWidgetScriptUrl;
			document.head.appendChild(script);
		});
	}
	await Promise.race([
		customElements.whenDefined("ppl-access-point-widget"),
		new Promise<never>((_resolve, reject) => {
			setTimeout(() => {
				reject(new Error("Timed out waiting for the PPL Access Point widget to be defined."));
			}, pplWidgetDefinitionTimeoutMs);
		}),
	]);
}

/**
 * You may need call {@link ensurePplAccessPointWidgetLoaded} before this function.
 * Prepares {@link HTMLPPLAccessPointWidgetElement} with the given configuration and API key.
 *
 * @param parentElement The parent element to append the widget to.
 * @param config The widget configuration.
 * @param apiKey The API key for the widget.
 * @returns A promise that resolves to the prepared widget element. Append it to the DOM to display it.
 */
export function prepareWidget(
	parentElement: HTMLElement,
	config: WidgetConfig,
	apiKey: string,
): Promise<HTMLPPLAccessPointWidgetElement> {
	return new Promise<HTMLPPLAccessPointWidgetElement>((resolve, reject) => {
		const widget = document.createElement("ppl-access-point-widget");
		widget.addEventListener(
			"ppl-accesspointwidget-ready",
			() => {
				resolve(widget);
			},
			{ once: true },
		);
		widget.addEventListener(
			"ppl-accesspointwidget-error",
			(event) => {
				reject(new Error(`PPL Access Point Widget error (code ${event.detail.code}): ${event.detail.message}`));
			},
			{ once: true },
		);
		widget.setAttribute("api-key", apiKey);
		widget.widgetConfig = config;
		parentElement.appendChild(widget);
	});
}

/**
 * Odvodí {@link DeliveryType} z výdejního místa vybraného ve widgetu.
 *
 * Typ místa přichází jako kód číselníku ("ALZA_BOX", "PPL_BOX_V2", "PPL_BOX_V3", …),
 * který PPL verzuje — proto se testuje podřetězec "BOX", ne výčet hodnot.
 * Výběr z detailu nese kód v {@link DetailResponseModel.type}, výběr ze seznamu
 * v {@link DetailResponseModel.accessPointType}. Chybí-li obojí, rozhodne prefix
 * názvu ("ABOX …" pro AlzaBox, "PBOX …" pro PPL ParcelBox).
 */
export function getDeliveryTypeFromPplResponse(result: DetailResponseModel): DeliveryType {
	const typeCode = result.type ?? result.accessPointType;
	const isBox = typeCode ? typeCode.toUpperCase().includes("BOX") : /^[AP]BOX/u.test(result.name);
	return isBox ? DeliveryType.DeliveryToParcelBox : DeliveryType.DeliveryToParcelShop;
}
